import * as React from "react";
import { useNavigate } from "react-router";
import { ArrowRight } from "lucide-react";
import { useEsgPortalConfig } from "./esg-portal.api";
import { Loader } from "@/components/ui/loader";
import type { EsgBoxConfig } from "@/types/organization";

interface Props {
  box: EsgBoxConfig;
  index: number;
  onClick: () => void;
}

const EsgCard: React.FC<Props> = ({ box, index, onClick }) => (
  <div
    onClick={onClick}
    style={{ animationDelay: `${(index + 1) * 100}ms` }}
    className="duration-[250ms] group relative flex w-80 animate-[fadeUp_0.6s_ease_both] cursor-pointer flex-col gap-5 overflow-hidden rounded-[20px] border border-[#e1ddd6] bg-white p-9 pb-7 shadow-[0_2px_8px_rgba(0,0,0,0.06)] transition-all ease-out hover:-translate-y-1.5 hover:border-[#d1ccc4] hover:shadow-[0_20px_60px_rgba(0,0,0,0.12)]"
  >
    <div className="duration-[250ms] absolute inset-x-0 top-0 h-[3px] rounded-t-[20px] bg-gradient-to-r from-[#c9a84c] to-[#fa8072] opacity-0 transition-opacity group-hover:opacity-100" />

    <div className="flex h-[72px] items-center">
      <div>
        <div className="font-['Playfair_Display',Georgia,serif] text-[28px] font-semibold tracking-wide text-[#2c2825]">
          {box.label}
        </div>
      </div>
    </div>

    <div className="h-px bg-[#e1ddd6]" />

    <div className="flex flex-col gap-3">
      {box.sector && (
        <div className="text-[11px] font-bold uppercase tracking-[0.16em] text-[#6b6560]">{box.sector}</div>
      )}
      {box.description && <p className="text-sm font-medium leading-relaxed text-[#2c2825]">{box.description}</p>}
    </div>

    <div className="flex items-center justify-between rounded-[10px] border border-[#e1ddd6] bg-[#f1ede9] px-4 py-3 text-[13px] font-semibold text-[#2c2825] transition-colors duration-200 hover:border-[#d1ccc4] hover:bg-[#ebe7e2]">
      Research
      <ArrowRight size={16} className="text-[#fa8072] transition-transform duration-200 group-hover:translate-x-1" />
    </div>
  </div>
);

export const EsgPortalView: React.FC = () => {
  const navigate = useNavigate();
  const boxes = useEsgPortalConfig();

  if (boxes.length === 0) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader className="h-8 w-8 text-coral" />
      </div>
    );
  }

  return (
    <div className="relative flex min-h-full flex-col items-center justify-center overflow-hidden px-6 py-16">
      <div className="pointer-events-none fixed inset-0 bg-[radial-gradient(ellipse_60%_50%_at_20%_20%,rgba(201,168,76,0.06)_0%,transparent_70%),radial-gradient(ellipse_50%_40%_at_80%_80%,rgba(250,128,114,0.07)_0%,transparent_70%)]" />

      <header className="relative mb-14 animate-[fadeUp_0.6s_ease_both] text-center">
        <div className="mb-4 inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.18em] text-[#6b6560]">
          <span className="inline-block h-px w-7 bg-[#d1ccc4]" />
          Company Research
          <span className="inline-block h-px w-7 bg-[#d1ccc4]" />
        </div>
        <h1 className="font-['Playfair_Display',Georgia,serif] text-[clamp(36px,5vw,56px)] font-semibold leading-tight tracking-tight text-[#2c2825]">
          ESG <span className="text-[#c9a84c]">Research</span>
        </h1>
        <p className="mx-auto mt-3.5 max-w-[480px] text-[15px] font-medium leading-relaxed text-[#6b6560]">
          Environmental, Social & Governance analysis across your selected portfolio companies.
        </p>
      </header>

      <div className="relative flex max-w-[1100px] flex-wrap justify-center gap-7">
        {boxes.map((box, index) => (
          <EsgCard key={box.id} box={box} index={index} onClick={() => navigate(`/esg/${box.id}`)} />
        ))}
      </div>

      <div className="relative mt-10 text-center">
        <p className="mb-2.5 text-[11px] font-bold uppercase tracking-[0.18em] text-[#6b6560]">Powered by</p>
        <img src="/images/pathfinder_logo_beta.svg" alt="Pathfinder" className="mx-auto h-8" />
      </div>
    </div>
  );
};
